import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { alpha, Box, Button, Dialog, Stack, TextField, Typography } from '@mui/material'
import type { CreateTrustedActorDto } from '@/core/models/trusted-actor.model'

const cleanFilledInputSx = {
  '& .MuiFilledInput-root': {
    borderRadius: 2,
    bgcolor: 'action.hover',
    transition: 'box-shadow 0.15s',
    '&:hover': { bgcolor: 'action.hover' },
    '&.Mui-focused': {
      bgcolor: 'action.hover',
      boxShadow: (theme: any) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`,
    },
    '&.Mui-error': {
      boxShadow: (theme: any) => `0 0 0 2px ${alpha(theme.palette.error.main, 0.35)}`,
    },
  },
}

interface TrustedActorDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (dto: CreateTrustedActorDto) => void | Promise<void>
  submitting?: boolean
  initialValues?: CreateTrustedActorDto
  mode?: 'create' | 'edit'
}

export function TrustedActorDialog({ open, onClose, onSubmit, submitting, initialValues, mode = 'create' }: TrustedActorDialogProps) {
  useLingui()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateTrustedActorDto>({
    defaultValues: { name: '', description: '' },
  })

  useEffect(() => {
    if (open) reset(initialValues ?? { name: '', description: '' })
  }, [open, initialValues, reset])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' },
      }}
    >
      <form onSubmit={handleSubmit(async (data) => onSubmit(data))}>
        {/* Hero header */}
        <Box
          sx={{
            px: 4,
            py: 3.5,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, letterSpacing: '-0.01em' }}>
            {mode === 'edit' ? <Trans>Edit a trusted actor</Trans> : <Trans>Add a trusted actor</Trans>}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.5 }}>
            <Trans>Register an external healthcare establishment or actor with whom your facility has a trust contract for INS identity exchange.</Trans>
          </Typography>
        </Box>

        {/* Form section */}
        <Stack spacing={3} sx={{ p: 3 }}>
          <Stack spacing={1}>
            <Typography
              component="label"
              htmlFor="trusted-actor-name"
              sx={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary' }}
            >
              <Trans>Name</Trans>
            </Typography>
            <TextField
              {...register('name', {
                required: t`Name is required`,
                minLength: { value: 2, message: t`Name must be at least 2 characters` },
                maxLength: { value: 128, message: t`Name is too long` },
              })}
              id="trusted-actor-name"
              placeholder={t`E.g. CH Saint-Joseph, e-Sante Bretagne…`}
              error={!!errors.name}
              helperText={errors.name?.message}
              inputProps={{ maxLength: 128 }}
              InputProps={{ disableUnderline: true }}
              variant="filled"
              hiddenLabel
              fullWidth
              autoFocus
              size="small"
              sx={cleanFilledInputSx}
            />
          </Stack>

          <Stack spacing={1}>
            <Typography
              component="label"
              htmlFor="trusted-actor-description"
              sx={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary' }}
            >
              <Trans>Description (optional)</Trans>
            </Typography>
            <TextField
              {...register('description', { maxLength: { value: 500, message: t`Description is too long` } })}
              id="trusted-actor-description"
              placeholder={t`E.g. Regional partner platform, neighbouring hospital, GHT member…`}
              error={!!errors.description}
              helperText={errors.description?.message}
              inputProps={{ maxLength: 500 }}
              InputProps={{ disableUnderline: true }}
              variant="filled"
              hiddenLabel
              multiline
              minRows={3}
              maxRows={5}
              fullWidth
              size="small"
              sx={cleanFilledInputSx}
            />
          </Stack>
        </Stack>

        {/* Action bar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, pb: 3 }}>
          <Button onClick={onClose} sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500 }}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={submitting}
            sx={{
              px: 4,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: (theme) => `0 6px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
            }}
          >
            {mode === 'edit' ? <Trans>Save</Trans> : <Trans>Add</Trans>}
          </Button>
        </Box>
      </form>
    </Dialog>
  )
}
